import type { Storyboard } from './storyboard';

export type Platform = 'reels' | 'tiktok' | 'shorts';
export type Tone = 'punchy' | 'aspirational' | 'practical' | 'founder-led';

export type JobStatus =
  | 'brief'
  | 'script_generating'
  | 'script_ready'
  | 'variant_approved'
  | 'audio_generating'
  | 'render_queued'
  | 'rendering'
  | 'done'
  | 'failed';

// ElevenLabs models exposed in the dashboard picker. Mirrors
// `ELEVENLABS_MODELS` in `marketing/functions/_voiceover.ts`.
export const ELEVENLABS_MODELS = [
  'eleven_turbo_v2_5',
  'eleven_multilingual_v2',
  'eleven_turbo_v2',
  'eleven_monolingual_v1',
] as const;

export type ElevenLabsModel = (typeof ELEVENLABS_MODELS)[number];

// Default voice and model, used as form placeholders. Mirrors
// `DEFAULT_ELEVENLABS_VOICE` / `DEFAULT_ELEVENLABS_MODEL` in
// `marketing/functions/_voiceover.ts`.
export const DEFAULT_VOICE_ID = '56bWURjYFHyYyVf490Dp'; // Emma
export const DEFAULT_MODEL: ElevenLabsModel = 'eleven_turbo_v2_5';

// UGC character profile (issue #589). Workspace-level persona reused
// across campaigns; mirrors the `marketing_characters` row shape
// returned by /api/marketing/characters.
export interface Character {
  id: string;
  owner_id: string;
  name: string;
  description: string;
  higgsfield_character_id: string | null;
  reference_image_url: string | null;
  // Free-form key/value persona attributes. Values are kept to the
  // primitives the dashboard's flat editor produces.
  traits: Record<string, string | number | boolean>;
  created_at: string;
  updated_at: string;
}

export interface CampaignBrief {
  name: string;
  product: string;
  audience: string;
  offer: string;
  proof: string;
  default_cta: string;
}

export interface ScriptRequest extends CampaignBrief {
  platform: Platform;
  tone: Tone;
  duration_seconds: number;
  // Voiceover config — optional, stored on `marketing_campaigns
  // .brand_settings` when the campaign is created. Server falls back
  // to Emma + eleven_turbo_v2_5 if either is blank.
  voice_id?: string;
  model?: ElevenLabsModel;
}

export interface VoiceoverResponse {
  job_id: string;
  variant_id: string;
  audio_segment_id: string;
  provider: 'elevenlabs' | 'openai';
  voice_id: string;
  model: string;
  duration_ms: number;
  captions_count: number;
  status: JobStatus;
  // Non-null when the endpoint silently fell back from ElevenLabs to
  // OpenAI (only on 5xx / transport errors / missing key — 4xx are
  // surfaced as a popup). Dashboard banner echoes this.
  fallback_reason: string | null;
}

export interface ScriptVariant {
  id: string;
  hook: string;
  caption: string;
  voiceover_text: string;
  storyboard: Storyboard;
}

export interface ScriptResponse {
  job_id: string;
  campaign_id: string;
  variants: ScriptVariant[];
}

export interface JobStatusResponse {
  id: string;
  status: JobStatus;
  selected_variant_id: string | null;
  error_message: string | null;
  render_url: string | null;
  thumbnail_url: string | null;
  updated_at: string;
}
